<template>
  <div class="vast-service-order-now-url-input">
    <div class="vast-service-order-now-url-input__container">
      <div class="vast-service-order-now-url-input__field-wrapper">
        <OInput
          v-model="urlModel"
          class="vast-service-order-now-url-input__field"
          :label="urlLabelTitle"
          :placeholder="placeholder"
          :errors="errors"
          :loading="loading"
          @input="handleInput"
          @focus="handleFocus"
          @blur="sendgridUrl"
          @keydown="keydownUrlInput"
          large
        />

        <p
          v-if="isUrlAdded && selectedNetwork?.id"
          class="vast-service-order-now-url-input__success-message"
        >
          {{ locale.successMessage }}
        </p>

        <YoutubeSearchResults
          v-if="canSearchByKeyword"
          :visible="searchResultsShown"
          :loading="searchLoading"
          :error="searchError"
          :results="searchResults"
          :selected-url="selectedVideoUrl"
          @select="selectSearchResult"
          @close="closeSearchResults"
        />
      </div>
      <span class="vast-service-order-now-url-input__info-text">
        {{ locale.urlInformation }}
      </span>
    </div>

    <p
      v-show="networkIsChangedFromLink.text"
      class="vast-service-order-now-url-input__network-change-from-url"
    >
      {{ locale.networkIsChangeFromUrlBefore }}
      {{ selectedNetworkName }}
      {{ locale.networkIsChangeFromUrlAfter }}
    </p>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import debounce from 'debounce';
import useStoreBinding from '@/mixins/use-store-binding';
import YoutubeSearchResults from './YoutubeSearchResults.vue';

export default {
  name: 'UrlInput',
  components: {
    YoutubeSearchResults,
  },
  mixins: [
    useStoreBinding('url', 'url.url', 'url/SET_URL'),
  ],
  inject: [
    'widgetJSApiInstance',
    'api',
  ],
  data() {
    return {
      isUrlAdded: false,
      searchText: '',
      searchResults: [],
      searchLoading: false,
      searchError: false,
      searchResultsShown: false,
      selectedVideoUrl: null,
    };
  },
  created() {
    this.widgetJSApiInstance.addActionCallback('getURL', () => this.url);

    this.widgetJSApiInstance.addActionCallback('setURL', (url) => {
      this.url = url;
      this.urlModel = url;
      this.isUrlAdded = true;
    });

    this.networkChangedFromLinkTextDebounce = debounce(
      this.networkChangedFromLinkText,
      2000,
    );

    this.searchDebounce = debounce(this.searchByKeyword, 600);
  },
  watch: {
    // eslint-disable-next-line
    'networkIsChangedFromLink.color' (newVal) {
      if (newVal) return;
      this.networkChangedFromLinkTextDebounce();
    },
  },
  computed: {
    ...mapState('url', [
      'loading',
    ]),
    ...mapState({
      creatingErrors: (state) => state.errors,
      networkIsChangedFromLink: (state) => state.networkIsChangedFromLink,
    }),
    ...mapState('url', {
      urlCheckErrors: (state) => state.errors,
    }),
    ...mapState('config', [
      'networksAreShown',
    ]),
    ...mapGetters('config', [
      'locale',
    ]),
    ...mapGetters('networks', [
      'networkId',
    ]),
    ...mapGetters([
      'selectedNetwork',
    ]),
    errors() {
      return [
        ...(this.urlCheckErrors.url || []),
        ...(this.creatingErrors.url || []),
      ];
    },
    placeholder() {
      return this.selectedNetwork ? this.selectedNetwork.urlPlaceholder : '';
    },
    selectedNetworkName() {
      return this.selectedNetwork ? this.selectedNetwork.name : '';
    },
    urlModel: {
      get() {
        return this.url;
      },
      set(val) {
        this.isUrlAdded = false;
        this.$store.dispatch('networks/identifyNetworkAndFetchServiceTypes', val);
        this.widgetJSApiInstance.dispatchEvent('onURLInput', val);
        this.url = val;
      },
    },
    urlLabelTitle() {
      return this.networksAreShown
        ? this.locale.urlLabel
        : this.locale.urlLabelWithoutNetworks;
    },
    canSearchByKeyword() {
      const network = this.selectedNetwork;
      if (!network) return false;

      return Boolean(network.hasSearch);
    },
  },
  methods: {
    stopSearchAndReset() {
      this.searchDebounce?.clear?.();
      this.searchDebounce?.cancel?.();
      this.resetSearchState();
    },
    clearErrors() {
      this.urlCheckErrors.url = [];
      this.creatingErrors.url = [];
    },
    sendgridUrl() {
      this.api.sendgridUrl();
    },
    networkChangedFromLinkText() {
      this.$store.dispatch('networkChangedFromLinkText', false);
    },
    keydownUrlInput(event) {
      const isCtrlPressed = (event.ctrlKey || event.metaKey);
      const isVPressed = (event.key === 'v' || event.code === 'KeyV');

      if (isCtrlPressed && isVPressed) {
        this.widgetJSApiInstance.dispatchEvent('onURLInsert');
      }
    },

    handleInput(value) {
      this.clearErrors();

      const trimmed = (value || '').trim();
      const shouldReset = !trimmed || !this.canSearchByKeyword;

      if (shouldReset) {
        this.stopSearchAndReset();
        return;
      }

      this.searchDebounce(trimmed);
    },

    handleFocus() {
      if (!this.canSearchByKeyword) return;

      const hasQuery = (this.searchText || '').trim().length > 0;

      if (hasQuery && this.searchResults.length) {
        this.openSearchResults();
        return;
      }

      if (hasQuery && !this.searchLoading) {
        this.searchDebounce(this.searchText);
      }
    },

    resetSearchState() {
      this.searchText = '';
      this.searchResults = [];
      this.searchLoading = false;
      this.searchError = false;
      this.closeSearchResults();
      this.selectedVideoUrl = null;
    },

    async searchByKeyword(query) {
      this.searchText = query;
      this.searchError = false;

      if (!query) {
        this.resetSearchState();
        return;
      }

      this.openSearchResults();
      this.searchLoading = true;

      try {
        const networkId = this.selectedNetwork?.id;

        if (!networkId) {
          this.searchResults = [];
          return;
        }

        const results = await this.api.searchPromotable({
          query,
          networkId,
        });

        this.searchResults = Array.isArray(results) ? results : [];
      } catch (e) {
        this.searchError = true;
        this.searchResults = [];
      } finally {
        this.searchLoading = false;
      }
    },

    closeSearchResults() {
      this.searchResultsShown = false;
    },
    openSearchResults() {
      this.searchResultsShown = true;
    },

    selectSearchResult(item) {
      const url = item?.url;
      if (!url) return;

      this.selectedVideoUrl = url;
      this.urlModel = url;

      if (this.selectedNetwork?.id) this.isUrlAdded = true;

      this.closeSearchResults();
    },
  },
};
</script>

<style lang="scss" scoped>
.vast-service-order-now-url-input {
  margin-top: 20px;
  &__network-change-from-url {
    color: #ffb116;
  }
  &__container {
    display: flex;
    align-items: center;

    @media (max-width: 768px) {
      flex-wrap: wrap;
      .vast-service-order-now-url-input__success-message {
        margin-bottom: 14px;
      }
    }
  }

  &__field-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
  }
  &__info-text {
    min-width: 239px;
    margin-left: 14px;
    color: var(--text-color);
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;

    @media (max-width: 768px) {
      width: 100%;
      margin-left: 0;
    }
  }
  &__field {
    margin-bottom: 0 !important;
  }
  &__success-message {
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    color: #4caf50;
    margin: 0;
  }
}
</style>
